'use client';

import { useState, useCallback, useMemo } from 'react';
import { ChevronRightIcon } from '@heroicons/react/20/solid';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import type { TreeNode, CheckedState } from '@/lib/platform/permissions/types';

export interface PermissionsTreeProps {
  data: TreeNode[];
  value?: string[];
  onChange?: (selectedIds: string[]) => void;
  className?: string;
}

// Module-scope pure recursive helpers (React Compiler immutability rule
// disallows self-referencing useCallback functions)

// Find parent node with independentSelection flag
function findParentWithIndependentSelection(
  targetId: string,
  nodes: TreeNode[],
  parent?: TreeNode
): TreeNode | null {
  for (const node of nodes) {
    if (node.id === targetId) {
      return parent && parent.independentSelection ? parent : null;
    }
    if (node.children) {
      const found = findParentWithIndependentSelection(targetId, node.children, node);
      if (found) return found;
    }
  }
  return null;
}

function PermissionsTree({ data, value = [], onChange, className = '' }: PermissionsTreeProps) {
  const { darkMode } = useDarkMode();
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Convert array of selected IDs to object for faster lookup
  const checkedState = useMemo(() => {
    const state: CheckedState = {};
    value.forEach((id) => {
      state[id] = true;
    });
    return state;
  }, [value]);

  // Pre-calculate all descendant and leaf IDs for each node (only depends on tree structure, not selection)
  const nodeDescendants = useMemo(() => {
    const descendants = new Map<string, string[]>();
    const leafDescendants = new Map<string, string[]>();

    const calculateDescendants = (node: TreeNode): { all: string[]; leaves: string[] } => {
      if (!node.children || node.children.length === 0) {
        // Leaf node
        const result = { all: [node.id], leaves: [node.id] };
        descendants.set(node.id, result.all);
        leafDescendants.set(node.id, result.leaves);
        return result;
      }

      // Parent node - collect from children
      const allIds: string[] = [node.id];
      const leafIds: string[] = [];

      node.children.forEach((child) => {
        const childResult = calculateDescendants(child);
        allIds.push(...childResult.all);
        leafIds.push(...childResult.leaves);
      });

      const result = { all: allIds, leaves: leafIds };
      descendants.set(node.id, result.all);
      leafDescendants.set(node.id, result.leaves);
      return result;
    };

    data.forEach((node) => calculateDescendants(node));

    return { descendants, leafDescendants };
  }, [data]);

  // Get all descendant IDs of a node (pre-calculated)
  const getDescendantIds = useCallback(
    (node: TreeNode): string[] => {
      return nodeDescendants.descendants.get(node.id) || [node.id];
    },
    [nodeDescendants]
  );

  // Get only leaf descendant IDs (pre-calculated)
  const getLeafDescendantIds = useCallback(
    (node: TreeNode): string[] => {
      return nodeDescendants.leafDescendants.get(node.id) || [node.id];
    },
    [nodeDescendants]
  );

  // Get direct descendant IDs only (respects independentSelection flag)
  const getDirectDescendantIds = useCallback(
    (node: TreeNode): string[] => {
      const ids: string[] = [node.id];
      // If node has independent selection, don't include children
      if (node.children && !node.independentSelection) {
        node.children.forEach((child) => {
          ids.push(...getDescendantIds(child));
        });
      }
      return ids;
    },
    [getDescendantIds]
  );

  // Get all leaf IDs in the tree (only nodes without children)
  const getAllLeafIds = useCallback(
    (nodes: TreeNode[]): string[] => {
      const ids: string[] = [];
      nodes.forEach((node) => {
        ids.push(...getLeafDescendantIds(node));
      });
      return ids;
    },
    [getLeafDescendantIds]
  );

  // Pre-calculate checked and indeterminate states for all nodes
  const nodeStates = useMemo(() => {
    const states = new Map<string, { checked: boolean; indeterminate: boolean }>();
    const adminChecked = checkedState['admin'] === true;

    const calculateState = (node: TreeNode): { checked: boolean; indeterminate: boolean } => {
      // Check if already calculated (memoization within this calculation)
      const cached = states.get(node.id);
      if (cached) return cached;

      let checked = false;
      let indeterminate = false;

      if (adminChecked) {
        // Admin selected = everything is checked, nothing is indeterminate
        checked = true;
        indeterminate = false;
      } else if (node.children && node.children.length > 0) {
        // Parent node - check based on leaf children
        const leafIds = node.children.flatMap((child) => getLeafDescendantIds(child));
        const checkedLeaves = leafIds.filter((id) => checkedState[id]);

        checked = leafIds.length > 0 && checkedLeaves.length === leafIds.length;
        indeterminate = checkedLeaves.length > 0 && checkedLeaves.length < leafIds.length;
      } else {
        // Leaf node
        checked = checkedState[node.id] === true;
        indeterminate = false;
      }

      const state = { checked, indeterminate };
      states.set(node.id, state);
      return state;
    };

    // Calculate states for all nodes recursively
    const traverse = (nodes: TreeNode[]) => {
      nodes.forEach((node) => {
        calculateState(node);
        if (node.children) {
          traverse(node.children);
        }
      });
    };

    traverse(data);
    return states;
  }, [checkedState, data, getLeafDescendantIds]);

  // Check if node is indeterminate (some but not all children are checked)
  const isIndeterminate = useCallback(
    (node: TreeNode): boolean => {
      return nodeStates.get(node.id)?.indeterminate ?? false;
    },
    [nodeStates]
  );

  // Check if node is fully checked (all descendants are checked)
  const isChecked = useCallback(
    (node: TreeNode): boolean => {
      return nodeStates.get(node.id)?.checked ?? false;
    },
    [nodeStates]
  );

  // Toggle node selection
  const toggleNode = useCallback(
    (node: TreeNode) => {
      // Special handling for 'admin' node - acts as "Select All" toggle
      if (node.id === 'admin') {
        const allLeafIds = getAllLeafIds(data);
        const adminChecked = checkedState['admin'];

        if (adminChecked) {
          // Deselect all
          onChange?.([]);
        } else {
          // Select all (only leaf nodes with role mappings)
          onChange?.(allLeafIds);
        }
        return;
      }

      // Use getDirectDescendantIds to respect independentSelection flag
      const descendantIds = getDirectDescendantIds(node);

      // For parent nodes, only check LEAF children (not intermediate parents, as only leaf nodes have role mappings)
      // For leaf nodes, check the node itself
      const idsToCheck =
        node.children && node.children.length > 0
          ? node.children.flatMap((child) => getLeafDescendantIds(child))
          : [node.id];
      const allChecked = idsToCheck.every((id) => checkedState[id]);

      let newSelected: string[];
      if (allChecked) {
        // Uncheck all descendants
        newSelected = value.filter((id) => !descendantIds.includes(id));

        // If node has independentSelection and children, also deselect all children when parent is deselected
        if (node.independentSelection && node.children) {
          const childIds = node.children.flatMap((child) => getDescendantIds(child));
          newSelected = newSelected.filter((id) => !childIds.includes(id));
        }

        // Note: We don't need to deselect parent nodes because they are never selected
        // (parent nodes are organizational categories without role mappings)

        // Special handling: If any permission is deselected, also deselect 'admin' (admin means full access)
        if (newSelected.includes('admin')) {
          // Check if ALL non-admin permissions are still selected
          const allLeafIds = getAllLeafIds(data);
          const allLeafIdsExceptAdmin = allLeafIds.filter((id) => id !== 'admin');
          const allStillSelected = allLeafIdsExceptAdmin.every((id) => newSelected.includes(id));

          // If not all selected, remove 'admin'
          if (!allStillSelected) {
            newSelected = newSelected.filter((id) => id !== 'admin');
          }
        }
      } else {
        // Check all descendants
        const toAdd = descendantIds.filter((id) => !checkedState[id]);
        newSelected = [...value, ...toAdd];

        // If this node's parent has independentSelection, also select the parent (dependency)
        const parentWithIndependent = findParentWithIndependentSelection(node.id, data);
        if (parentWithIndependent && !checkedState[parentWithIndependent.id]) {
          newSelected.push(parentWithIndependent.id);
        }

        // Note: We don't auto-select parent nodes because they are organizational categories
        // without role mappings (e.g., 'valuations', 'sponsorships', 'platform').
        // Only leaf nodes with actual role mappings should be in the selection.

        // Check if ALL permissions (except admin) are now selected (auto-select admin)
        const allLeafIds = getAllLeafIds(data);
        const allLeafIdsExceptAdmin = allLeafIds.filter((id) => id !== 'admin');
        const allSelected = allLeafIdsExceptAdmin.every((id) => newSelected.includes(id));

        if (allSelected && !newSelected.includes('admin')) {
          newSelected.push('admin');
        }
      }

      onChange?.(newSelected);
    },
    [
      value,
      checkedState,
      getDirectDescendantIds,
      getDescendantIds,
      getLeafDescendantIds,
      data,
      onChange,
      getAllLeafIds,
    ]
  );

  // Toggle expand/collapse
  const toggleExpand = useCallback((nodeId: string) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  return (
    <div className={className}>
      {/* Tree */}
      <div className={`divide-y ${darkMode ? 'divide-white/10' : 'divide-gray-200'}`}>
        {data.map((node) => (
          <div key={node.id} className="py-2 first:pt-0 last:pb-0">
            <TreeNodeComponent
              node={node}
              level={0}
              checked={isChecked(node)}
              indeterminate={isIndeterminate(node)}
              expanded={expandedNodes.has(node.id)}
              onToggle={() => toggleNode(node)}
              onExpandToggle={() => toggleExpand(node.id)}
              getCheckedState={isChecked}
              getIndeterminateState={isIndeterminate}
              onNodeToggle={toggleNode}
              isExpanded={(id) => expandedNodes.has(id)}
              onNodeExpandToggle={toggleExpand}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

interface TreeNodeComponentProps {
  node: TreeNode;
  level: number;
  checked: boolean;
  indeterminate: boolean;
  expanded: boolean;
  onToggle: () => void;
  onExpandToggle: () => void;
  getCheckedState: (node: TreeNode) => boolean;
  getIndeterminateState: (node: TreeNode) => boolean;
  onNodeToggle: (node: TreeNode) => void;
  isExpanded: (id: string) => boolean;
  onNodeExpandToggle: (id: string) => void;
}

function TreeNodeComponent({
  node,
  level,
  checked,
  indeterminate,
  expanded,
  onToggle,
  onExpandToggle,
  getCheckedState,
  getIndeterminateState,
  onNodeToggle,
  isExpanded,
  onNodeExpandToggle,
}: TreeNodeComponentProps) {
  const { darkMode } = useDarkMode();
  const hasChildren = node.children && node.children.length > 0;

  return (
    <div>
      {/* Node Row */}
      <div
        className={`flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors ${
          darkMode ? 'hover:bg-white/5' : 'hover:bg-gray-100'
        }`}
        style={{ paddingLeft: `${level * 1.5 + 0.5}rem` }}
      >
        {/* Expand/Collapse Button */}
        {hasChildren ? (
          <button
            type="button"
            onClick={onExpandToggle}
            aria-label={expanded ? 'Collapse' : 'Expand'}
            className={`flex-shrink-0 rounded p-0.5 transition-transform ${
              expanded ? 'rotate-90' : ''
            } ${
              darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <ChevronRightIcon className="size-4" />
          </button>
        ) : (
          <div className="size-5 flex-shrink-0" />
        )}

        {/* Checkbox */}
        <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={checked}
            ref={(el) => {
              if (el) {
                el.indeterminate = indeterminate;
              }
            }}
            onChange={onToggle}
            className={`size-4 cursor-pointer rounded border transition-colors ${
              darkMode
                ? 'border-white/10 bg-white/5 text-blue-500 focus:ring-blue-500 focus:ring-offset-gray-900'
                : 'border-gray-300 bg-white text-blue-600 focus:ring-blue-500'
            }`}
          />
          <span className={`truncate text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
            {node.label}
          </span>
        </label>
      </div>

      {/* Children */}
      {hasChildren && expanded && (
        <div>
          {node.children!.map((child) => (
            <TreeNodeComponent
              key={child.id}
              node={child}
              level={level + 1}
              checked={getCheckedState(child)}
              indeterminate={getIndeterminateState(child)}
              expanded={isExpanded(child.id)}
              onToggle={() => onNodeToggle(child)}
              onExpandToggle={() => onNodeExpandToggle(child.id)}
              getCheckedState={getCheckedState}
              getIndeterminateState={getIndeterminateState}
              onNodeToggle={onNodeToggle}
              isExpanded={isExpanded}
              onNodeExpandToggle={onNodeExpandToggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Export PermissionsTree
export default PermissionsTree;
